import React, { useEffect, useState } from 'react';
import { Container, Card, Alert, Spinner, Button } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import api, { CaseStudy, UserTier } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import MadeWithBadge from './MadeWithBadge';

const GlobeIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm7.5-6.923c-.67.204-1.335.82-1.887 1.855A7.97 7.97 0 0 0 5.145 4H7.5V1.077zM4.09 4a9.267 9.267 0 0 1 .64-1.539 6.7 6.7 0 0 1 .597-.933A7.025 7.025 0 0 0 2.255 4H4.09zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a6.958 6.958 0 0 0-.656 2.5h2.49zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5H4.847zM8.5 5v2.5h2.99a12.495 12.495 0 0 0-.337-2.5H8.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5H4.51zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5H8.5zM5.145 12c.138.386.295.744.468 1.068.552 1.035 1.218 1.65 1.887 1.855V12H5.145zm.182 2.472a6.696 6.696 0 0 1-.597-.933A9.268 9.268 0 0 1 4.09 12H2.255a7.024 7.024 0 0 0 3.072 2.472zM3.82 11a13.652 13.652 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5H3.82zm6.853 3.472A7.024 7.024 0 0 0 13.745 12H11.91a9.27 9.27 0 0 1-.64 1.539 6.688 6.688 0 0 1-.597.933zM8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855.173-.324.33-.682.468-1.068H8.5zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.65 13.65 0 0 1-.312 2.5zm2.802-3.5a6.959 6.959 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5h2.49zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7.024 7.024 0 0 0-3.072-2.472c.218.284.418.598.597.933zM10.855 4a7.966 7.966 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4h2.355z"/>
  </svg>
);

const ShareIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5z"/>
  </svg>
);

const CaseStudyDetail: React.FC = () => {
  const { id, userId, projectId } = useParams<{ id?: string; userId?: string; projectId?: string }>();
  const [caseStudy, setCaseStudy] = useState<CaseStudy | null>(null);
  const [ownerTier, setOwnerTier] = useState<UserTier | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();

  // Owner (or an admin viewing someone else's record) can edit. Legacy routes without
  // a userId in the URL only show the Edit button when the viewer is authenticated.
  const canEdit =
    auth.isAuthenticated && (auth.isAdmin || !userId || auth.userId === userId);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Support both legacy (/case-study/:id) and new (/userId/case-study/:projectId) routing
  const caseStudyId = projectId || id;

  useEffect(() => {
    if (caseStudyId) {
      loadCaseStudy();
    }
  }, [caseStudyId, userId]);

  const loadCaseStudy = async () => {
    try {
      const data = await api.getCaseStudy(caseStudyId!, userId);
      setCaseStudy(data);
      // Resolve owner tier so we can decide whether to render the free-tier badge.
      // Only possible on the user-scoped route — legacy /case-study/:id doesn't expose the owner.
      if (userId) {
        try {
          const company = await api.getCompany(userId);
          setOwnerTier(company.ownerTier ?? 'free');
        } catch (e) {
          setOwnerTier('free');
        }
      }
    } catch (err) {
      setError('Failed to load case study');
    } finally {
      setLoading(false);
    }
  };

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 1,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 1,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
    },
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error || !caseStudy) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error || 'Case study not found'}</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5 mb-5">
      <Card>
        <Card.Body>
          {caseStudy.photos && caseStudy.photos.length > 0 && (
            <div className="mb-4">
              <Carousel
                responsive={responsive}
                infinite={true}
                autoPlay={caseStudy.photos.length > 1}
                autoPlaySpeed={2500}
                keyBoardControl={true}
                showDots={true}
                removeArrowOnDeviceType={[]}
                customTransition="transform 500ms ease-in-out"
                transitionDuration={500}
              >
                {caseStudy.photos.map((photo, index) => (
                  <div key={index} className="carousel-image-container">
                    <img
                      src={photo.url}
                      alt={`${caseStudy.projectName} - Photo ${index + 1}`}
                      className="d-block w-100"
                      style={{
                        maxHeight: '500px',
                        objectFit: 'contain',
                        backgroundColor: '#f8f9fa',
                      }}
                    />
                  </div>
                ))}
              </Carousel>

              {/* Photo thumbnails under carousel */}
              {caseStudy.photos.length > 1 && (
                <div className="mt-3">
                  <h6 className="text-muted mb-2">All Photos ({caseStudy.photos.length})</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {caseStudy.photos.map((photo, index) => (
                      <img
                        key={index}
                        src={photo.url}
                        alt={`${caseStudy.projectName} - Thumbnail ${index + 1}`}
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          border: '2px solid #dee2e6',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s ease'
                        }}
                        className="thumbnail-photo"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#007bff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#dee2e6';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="d-flex justify-content-between align-items-start mb-3">
            <h1 className="mb-0">{caseStudy.projectName}</h1>
            <div className="d-flex gap-2 flex-wrap">
              {canEdit && caseStudyId && (
                <Link
                  to={userId ? `/${userId}/admin/case-study/edit/${caseStudyId}` : `/admin/case-study/edit/${caseStudyId}`}
                >
                  <Button variant="outline-primary" size="sm" title="Edit this case study">
                    Edit
                  </Button>
                </Link>
              )}
              {!caseStudy.hideFromMap && caseStudy.location?.coordinates[0] !== 0 && caseStudy.location?.coordinates[1] !== 0 && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const coords = caseStudy.location.coordinates;
                    const path = userId ? `/${userId}` : '/';
                    navigate(`${path}?lat=${coords[1]}&lng=${coords[0]}`);
                  }}
                  title="View on map"
                >
                  <GlobeIcon /> View on Map
                </Button>
              )}
              <Button
                variant={copied ? "success" : "outline-secondary"}
                size="sm"
                onClick={handleShare}
                title="Copy link to clipboard"
              >
                <ShareIcon /> {copied ? 'Copied!' : 'Share'}
              </Button>
            </div>
          </div>
          
          {caseStudy.country && (
            <div className="mb-4">
              <p className="mb-2">
                <strong>Country:</strong> {caseStudy.country}
              </p>
            </div>
          )}

          <div className="project-description">
            {/*<h3 className="mb-3">Description</h3>*/}
            <ReactMarkdown remarkPlugins={[remarkBreaks]} allowedElements={['p', 'strong', 'em', 'br', 'ul', 'ol', 'li']}>
              {caseStudy.description}
            </ReactMarkdown>
          </div>

          {ownerTier === 'free' && (
            <div className="d-flex justify-content-center">
              <MadeWithBadge variant="inline" />
            </div>
          )}

          <Link to={userId ? `/${userId}` : '/'} className="d-block mt-4">
            <Button variant="primary" size="lg" className="w-100">
              <GlobeIcon /> Explore the Full Map
            </Button>
          </Link>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CaseStudyDetail;